import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { Departamento, Membro, PapelMembro } from '@sgc/contracts';
import { del, get, post, put } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Area, Campo, Check, Sel } from '../ui/campos';
import { Modal, Pill, Sec, Tabs } from '../ui/layout';
import { useToast } from '../ui/toast';
import { ModalCarteira } from '../telas/equipe/carteira';

export const Route = createFileRoute('/_app/equipe')({ component: Equipe });

const PAPEIS: ReadonlyArray<readonly [PapelMembro, string]> = [
  ['ADMIN', 'Administrador'], ['SUPERVISOR', 'Supervisor'], ['OPERADOR', 'Operador'], ['LEITOR', 'Somente leitura'],
];
const rotuloPapel = (p: PapelMembro) => PAPEIS.find(([v]) => v === p)?.[1] ?? p;

function Equipe() {
  const [aba, setAba] = useState<'membros' | 'departamentos'>('membros');
  const { eu } = useAuth();
  const ehAdmin = eu?.escritorio?.papel === 'ADMIN';
  return (
    <>
      <div className="topbar">
        <div><h1>Equipe</h1><div className="sub">quem trabalha no escritório e em que departamento</div></div>
      </div>
      <div className="conteudo">
        <Tabs valor={aba} onChange={setAba} itens={[{ id: 'membros', rotulo: 'Membros' }, { id: 'departamentos', rotulo: 'Departamentos' }]} />
        {aba === 'membros' ? <Membros ehAdmin={ehAdmin} /> : <Departamentos ehAdmin={ehAdmin} />}
      </div>
    </>
  );
}

// ------------------------------------------------------------------ membros
type FormMembro = { nome: string; email: string; papel: PapelMembro; departamentoId: string; apelido: string; ativo: boolean; senha: string; responderPeloEmail: boolean };
const membroVazio: FormMembro = { nome: '', email: '', papel: 'OPERADOR', departamentoId: '', apelido: '', ativo: true, senha: '', responderPeloEmail: false };

function Membros({ ehAdmin }: { ehAdmin: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const membros = useQuery({ queryKey: ['membros'], queryFn: () => get<Membro[]>('/v1/membros') });
  const deps = useQuery({ queryKey: ['departamentos'], queryFn: () => get<Departamento[]>('/v1/departamentos') });
  const [edit, setEdit] = useState<{ id: string | null; f: FormMembro } | null>(null);
  const [carteira, setCarteira] = useState<Membro | null>(null);

  const salvar = useMutation({
    mutationFn: async ({ id, f }: { id: string | null; f: FormMembro }) => {
      const corpo = { ...f, departamentoId: f.departamentoId || null, apelido: f.apelido || null, senha: f.senha || undefined };
      if (id) { const { senha: _s, email: _e, ...resto } = corpo; return put<Membro>(`/v1/membros/${id}`, resto); }
      return post<Membro>('/v1/membros', corpo);
    },
    onSuccess: (_m, v) => { void qc.invalidateQueries({ queryKey: ['membros'] }); setEdit(null); toast.ok(v.id || v.f.senha ? 'membro salvo' : 'membro criado — convite enviado por e-mail'); },
    onError: (e) => toast.erro(e),
  });
  const reenviar = useMutation({
    mutationFn: (id: string) => post(`/v1/membros/${id}/convite`),
    onSuccess: () => toast.ok('convite reenviado (chega em até 1 minuto, se o SMTP estiver configurado)'),
    onError: (e) => toast.erro(e),
  });

  const nomeDep = (id: string | null) => deps.data?.find((d) => d.id === id)?.sigla ?? '—';

  return (
    <Sec titulo="Membros" sub={`${membros.data?.length ?? 0} pessoa(s)`}
      acoes={ehAdmin && <button className="btn mini primario" type="button" onClick={() => setEdit({ id: null, f: membroVazio })}>+ Novo membro</button>}>
      <div className="tbwrap">
        <table className="tb">
          <thead><tr><th>Nome</th><th>E-mail</th><th>Papel</th><th>Depto.</th><th>Apelido</th><th>Clientes que vê</th><th>Situação</th>{ehAdmin && <th />}</tr></thead>
          <tbody>
            {membros.isLoading && <tr><td className="vazio" colSpan={8}>carregando…</td></tr>}
            {membros.data?.map((m) => (
              <tr key={m.id}>
                <td>{m.nome}</td>
                <td className="dim">{m.email}</td>
                <td><Pill cor={m.papel === 'ADMIN' ? 'acento' : undefined}>{rotuloPapel(m.papel)}</Pill></td>
                <td>{nomeDep(m.departamentoId)}</td>
                <td className="mono">{m.apelido ?? ''}</td>
                <td>{m.papel === 'ADMIN' || m.papel === 'SUPERVISOR' || m.acessoClientes !== 'CARTEIRA' ? <span className="dim">todos</span> : <Pill cor="ambar">só a carteira · {m.clientesNaCarteira}</Pill>}</td>
                <td>{m.ativo ? <Pill cor="verde">ativo</Pill> : <Pill cor="verm">inativo</Pill>}</td>
                {ehAdmin && <td className="acoes"><button className="btn mini" type="button" onClick={() => setEdit({ id: m.id, f: { nome: m.nome, email: m.email, papel: m.papel, departamentoId: m.departamentoId ?? '', apelido: m.apelido ?? '', ativo: m.ativo, senha: '', responderPeloEmail: m.responderPeloEmail ?? false } })}>Editar</button>{' '}<button className="btn mini" type="button" title="quais clientes esta pessoa enxerga" onClick={() => setCarteira(m)}>Carteira</button>{' '}<button className="btn mini" type="button" title="manda um link para a pessoa definir a senha" disabled={reenviar.isPending} onClick={() => reenviar.mutate(m.id)}>Convite</button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {carteira && <ModalCarteira membro={carteira} onFechar={() => setCarteira(null)} />}

      <Modal titulo={edit?.id ? 'Editar membro' : 'Novo membro'} aberto={!!edit} onFechar={() => setEdit(null)}
        acoes={<>
          <button className="btn" type="button" onClick={() => setEdit(null)}>Cancelar</button>
          <button className="btn primario" type="button" disabled={salvar.isPending} onClick={() => edit && salvar.mutate(edit)}>Salvar</button>
        </>}>
        {edit && (
          <div className="fgrid">
            <Campo className="c7" rotulo="Nome" valor={edit.f.nome} onChange={(v) => setEdit({ ...edit, f: { ...edit.f, nome: v } })} obrigatorio autoFocus />
            <Campo className="c5" rotulo="Apelido (iniciais)" valor={edit.f.apelido} onChange={(v) => setEdit({ ...edit, f: { ...edit.f, apelido: v.toUpperCase().slice(0, 10) } })} ajuda="aparece nas células da matriz" />
            <Campo className="c7" rotulo="E-mail" valor={edit.f.email} onChange={(v) => setEdit({ ...edit, f: { ...edit.f, email: v } })} type="email" obrigatorio readOnly={!!edit.id} />
            <Sel className="c5" rotulo="Papel" valor={edit.f.papel} opcoes={PAPEIS} onChange={(v) => setEdit({ ...edit, f: { ...edit.f, papel: v as PapelMembro } })} />
            <Sel className="c7" rotulo="Departamento" valor={edit.f.departamentoId} vazio="— nenhum —" opcoes={(deps.data ?? []).map((d) => [d.id, `${d.sigla} · ${d.nome}`] as const)} onChange={(v) => setEdit({ ...edit, f: { ...edit.f, departamentoId: v } })} />
            {!edit.id && <Campo className="c5" rotulo="Senha inicial" valor={edit.f.senha} onChange={(v) => setEdit({ ...edit, f: { ...edit.f, senha: v } })} type="password" autoComplete="new-password" ajuda="vazio = a pessoa recebe um convite por e-mail para escolher a senha" />}
            <Check className="c12" rotulo="Ativo" valor={edit.f.ativo} onChange={(v) => setEdit({ ...edit, f: { ...edit.f, ativo: v } })} />
            <Check className="c12" rotulo="Nos envios que esta pessoa fizer sozinha, o cliente responde para o e-mail dela" valor={edit.f.responderPeloEmail} onChange={(v) => setEdit({ ...edit, f: { ...edit.f, responderPeloEmail: v } })} ajuda="desligado: a resposta vai para o e-mail do departamento ou do escritório (Configurações). O e-mail dela nunca aparece no corpo da mensagem — só como 'responder para'." />
          </div>
        )}
      </Modal>
    </Sec>
  );
}

// ------------------------------------------------------------------ departamentos
type FormDep = { nome: string; sigla: string; cor: string; ordem: number; ativo: boolean; emailResposta: string; assinaturaEmail: string };
const depVazio: FormDep = { nome: '', sigla: '', cor: '#4f56e8', ordem: 0, ativo: true, emailResposta: '', assinaturaEmail: '' };

function Departamentos({ ehAdmin }: { ehAdmin: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const deps = useQuery({ queryKey: ['departamentos'], queryFn: () => get<Departamento[]>('/v1/departamentos') });
  const [edit, setEdit] = useState<{ id: string | null; f: FormDep } | null>(null);

  const salvar = useMutation({
    mutationFn: ({ id, f }: { id: string | null; f: FormDep }) => { const corpo = { ...f, emailResposta: f.emailResposta.trim() || null, assinaturaEmail: f.assinaturaEmail.trim() || null }; return id ? put<Departamento>(`/v1/departamentos/${id}`, corpo) : post<Departamento>('/v1/departamentos', corpo); },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['departamentos'] }); setEdit(null); toast.ok('departamento salvo'); },
    onError: (e) => toast.erro(e),
  });
  const excluir = useMutation({
    mutationFn: (id: string) => del(`/v1/departamentos/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['departamentos'] }); toast.ok('departamento excluído'); },
    onError: (e) => toast.erro(e),
  });

  return (
    <Sec titulo="Departamentos" sub="fiscal, contábil, pessoal, societário…"
      acoes={ehAdmin && <button className="btn mini primario" type="button" onClick={() => setEdit({ id: null, f: depVazio })}>+ Novo departamento</button>}>
      <div className="tbwrap">
        <table className="tb">
          <thead><tr><th>Sigla</th><th>Nome</th><th className="c">Ordem</th><th>Situação</th>{ehAdmin && <th />}</tr></thead>
          <tbody>
            {deps.data?.length === 0 && <tr><td className="vazio" colSpan={5}>nenhum departamento ainda</td></tr>}
            {deps.data?.map((d) => (
              <tr key={d.id}>
                <td><span className="dot" style={{ background: d.cor ?? 'var(--text-faint)', marginRight: 8 }} /><b>{d.sigla}</b></td>
                <td>{d.nome}</td>
                <td className="c">{d.ordem}</td>
                <td>{d.ativo ? <Pill cor="verde">ativo</Pill> : <Pill cor="verm">inativo</Pill>}</td>
                {ehAdmin && <td className="acoes">
                  <button className="btn mini" type="button" onClick={() => setEdit({ id: d.id, f: { nome: d.nome, sigla: d.sigla, cor: d.cor ?? '#4f56e8', ordem: d.ordem, ativo: d.ativo, emailResposta: d.emailResposta ?? '', assinaturaEmail: d.assinaturaEmail ?? '' } })}>Editar</button>{' '}
                  <button className="btn mini perigo" type="button" onClick={() => { if (confirm(`Excluir ${d.nome}?`)) excluir.mutate(d.id); }}>Excluir</button>
                </td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal titulo={edit?.id ? 'Editar departamento' : 'Novo departamento'} aberto={!!edit} onFechar={() => setEdit(null)} estreito
        acoes={<>
          <button className="btn" type="button" onClick={() => setEdit(null)}>Cancelar</button>
          <button className="btn primario" type="button" disabled={salvar.isPending} onClick={() => edit && salvar.mutate(edit)}>Salvar</button>
        </>}>
        {edit && (
          <div className="fgrid">
            <Campo className="c8" rotulo="Nome" valor={edit.f.nome} onChange={(v) => setEdit({ ...edit, f: { ...edit.f, nome: v } })} obrigatorio autoFocus />
            <Campo className="c4" rotulo="Sigla" valor={edit.f.sigla} onChange={(v) => setEdit({ ...edit, f: { ...edit.f, sigla: v.toUpperCase().slice(0, 8) } })} obrigatorio />
            <Campo className="c4" rotulo="Cor" valor={edit.f.cor} onChange={(v) => setEdit({ ...edit, f: { ...edit.f, cor: v } })} type="color" />
            <Campo className="c4" rotulo="Ordem" valor={String(edit.f.ordem)} onChange={(v) => setEdit({ ...edit, f: { ...edit.f, ordem: Number(v) || 0 } })} type="number" />
            <Check className="c4" rotulo="Ativo" valor={edit.f.ativo} onChange={(v) => setEdit({ ...edit, f: { ...edit.f, ativo: v } })} />
            <Campo className="c12" rotulo="E-mail de resposta nos envios ao cliente" type="email" valor={edit.f.emailResposta} onChange={(v) => setEdit({ ...edit, f: { ...edit.f, emailResposta: v } })} placeholder="fiscal@escritorio.com.br" ajuda="quando o envio é só deste departamento, o cliente responde para aqui (vazio = o e-mail do escritório, em Configurações)" />
            <Area className="c12" rotulo="Assinatura dos e-mails deste departamento" valor={edit.f.assinaturaEmail} onChange={(v) => setEdit({ ...edit, f: { ...edit.f, assinaturaEmail: v } })} linhas={2} placeholder={'Departamento Fiscal · (11) 99999-0000'} ajuda="no rodapé do e-mail de envio só deste departamento; vazio = a assinatura do escritório" />
          </div>
        )}
      </Modal>
    </Sec>
  );
}
